import {
  InfiniteData,
  QueryClient,
  QueryKey,
  UseInfiniteQueryResult,
  UseQueryResult,
} from '@tanstack/react-query'

import { ApiError } from '@/context/usaspending/liveFetcher'

import { ensureDateStringIsUTC } from '@/helpers/date'

import type {
  PaginateRequest,
  PaginatedResponse,
  SortRequest,
} from '@/types/Api'
import type { HasID } from '@/types/Id'

export async function fetchJsonWithInit<T>(
  url: string,
  accessToken?: string | null,
  init?: RequestInit,
): Promise<T> {
  const headers = new Headers(init?.headers)
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`)
  }
  const response = await fetch(url, { ...init, headers })
  if (!response.ok) {
    let detail: string | undefined
    try {
      const payload = await response.json()
      if (
        payload &&
        typeof payload === 'object' &&
        'detail' in payload &&
        typeof payload.detail === 'string'
      ) {
        detail = payload.detail
      }
    } catch {
      // Ignore non-JSON error bodies and fall back to the HTTP status text.
    }
    throw new ApiError(response.status, response.statusText, detail)
  }
  return response.json()
}

export async function fetchJson<T>(
  url: string,
  accessToken?: string | null,
): Promise<T> {
  return fetchJsonWithInit<T>(url, accessToken)
}

export const buildPaginatedEndpoint = (
  url: string,
  pagination: PaginateRequest,
) => {
  let paginatedUrl = `${url}${url.includes('?') ? '&' : '?'}limit=${pagination.limit}`
  if (pagination.offset) {
    paginatedUrl = `${paginatedUrl}${paginatedUrl.includes('?') ? '&' : '?'}offset=${pagination.offset}`
  }
  return paginatedUrl
}

export const buildSortedEndpoint = (url: string, sort?: SortRequest) => {
  if (sort) {
    return `${url}${url.includes('?') ? '&' : '?'}order_by=${sort.order_by}&sort=${sort.sort ?? 'desc'}`
  }
  return url
}

export const handlePaginationParamsAfterDelete = <T extends HasID>(
  ids: Array<string>,
  previousData: InfiniteData<PaginatedResponse<T>, PaginateRequest>,
) => {
  // iterate through each pagination request and update any starting_after ids if the were deleted;
  // TODO: actually verify this works, doesn't seem very sound
  const updatedPageParams = previousData.pageParams.map((param, index) => {
    // if (param && param.starting_after && ids.includes(param.starting_after)) {
    //   let previousItem: T | undefined
    //   const data = previousData.pages[index]?.data
    //   if (data) {
    //     for (let i = data.length - 1; i >= 0; i--) {
    //       if (!ids.includes(data[i].id)) {
    //         previousItem = data[i]
    //         break
    //       }
    //     }
    //   }
    //   return { ...param, starting_after: previousItem?.id }
    // }
    return param
  })
  return updatedPageParams
}

export const getNextPageParam = <T extends HasID>(
  lastPage: PaginatedResponse<T>,
  allPages: Array<PaginatedResponse<T>>,
  lastPageParam: PaginateRequest,
  allPageParams: Array<PaginateRequest>,
) => {
  if (!lastPage.has_more) {
    return undefined
  }
  return {
    limit: lastPageParam.limit,
    offset: (lastPageParam.offset ?? 0) + lastPageParam.limit,
  }
}

export const insertAtBeginning = <T>(
  item: T,
  previousData: InfiniteData<PaginatedResponse<T>, PaginateRequest> | undefined,
) => {
  if (previousData) {
    return {
      ...previousData,
      pages: [
        {
          ...previousData.pages[0],
          data: [item, ...previousData.pages[0].data],
        },
        ...previousData.pages.slice(1),
      ],
    }
  }
  return {
    pages: [{ data: [item], count: 1, has_more: false }],
    pageParams: [],
  }
}

export const insertAtBeginningOfPage = <T>(
  newData: PaginatedResponse<T>,
  previousData: PaginatedResponse<T> | undefined,
) => {
  if (previousData) {
    return {
      ...previousData,
      data: [...newData.data, ...previousData.data],
      count: previousData.count + newData.count,
      has_more: previousData.has_more || newData.has_more,
    }
  }
  return newData
}

export const insertSingleAtBeginningOfPage = <T>(
  item: T,
  previousData: PaginatedResponse<T> | undefined,
) => {
  return insertAtBeginningOfPage(
    { count: 1, data: [item], has_more: false },
    previousData,
  )
}

export const insertAtEnd = <T>(
  item: T | Array<T>,
  previousData: InfiniteData<PaginatedResponse<T>, PaginateRequest> | undefined,
) => {
  const item_array: Array<T> = Array.isArray(item) ? item : [item]
  if (previousData) {
    const lastIndex = previousData.pages.length - 1
    return {
      ...previousData,
      pages: [
        ...previousData.pages.slice(0, -1),
        {
          ...previousData.pages[lastIndex],
          data: [...previousData.pages[lastIndex].data, ...item_array],
        },
      ],
    }
  }
  return {
    pages: [{ data: item_array, count: item_array.length, has_more: false }],
    pageParams: [],
  }
}

export const insertAtEndOfPage = <T>(
  newData: PaginatedResponse<T>,
  previousData: PaginatedResponse<T> | undefined,
) => {
  if (previousData) {
    return {
      ...previousData,
      data: [...previousData.data, ...newData.data],
      count: previousData.count + newData.count,
      has_more: previousData.has_more || newData.has_more,
    }
  }
  return newData
}

export const convertDatesInObject = (
  obj: any,
  keys: Array<string> = [
    'created_at',
    'last_modified',
    'last_interacted',
    'due_date',
    'trial_started_at',
    'trial_ends_at',
    'converted_at',
    'expires_at',
  ],
): any => {
  // TODO: this function mutates the original object;
  // i tried creating a new object and returning it, but it caused downstream infinite rerenders
  if (Array.isArray(obj)) {
    // if the current object is an array, map through its items
    return obj.map((item) => convertDatesInObject(item, keys))
  } else if (typeof obj === 'object' && obj !== null) {
    // if the current object is an object, iterate through its keys
    return Object.keys(obj).reduce((acc: any, key: string) => {
      const value = obj[key]
      if (keys.includes(key) && typeof value === 'string') {
        // if the key matches and the value is a string, attempt to parse it as a date
        const date = Date.parse(ensureDateStringIsUTC(value))
        acc[key] = !isNaN(date) ? new Date(date) : value
      } else if (typeof value === 'object') {
        // if the value is an object, recursively process it
        acc[key] = convertDatesInObject(value, keys)
      } else {
        // otherwise, copy the value as is
        acc[key] = value
      }
      return acc
    }, {})
  } else {
    // if the object is neither an array nor an object, return it directly
    return obj
  }
}

export const handleUpdateData = async <T extends HasID>(
  queryClient: QueryClient,
  key: QueryKey,
  obj: T,
) => {
  await queryClient.cancelQueries({ queryKey: key })

  // Cache may be InfiniteData (useInfiniteQuery) or PaginatedResponse (useQuery).
  const previousData = queryClient.getQueryData<
    InfiniteData<PaginatedResponse<T>> | PaginatedResponse<T>
  >(key)

  if (previousData && 'pages' in previousData) {
    const updatedPages = previousData.pages.map((page) => ({
      ...page,
      data: page.data.map((o) => (o.id === obj.id ? obj : o)),
    }))
    queryClient.setQueryData<InfiniteData<PaginatedResponse<T>>>(key, {
      ...previousData,
      pages: updatedPages,
    })
  } else if (previousData) {
    queryClient.setQueryData<PaginatedResponse<T>>(key, {
      ...previousData,
      data: previousData.data.map((o) => (o.id === obj.id ? obj : o)),
    })
  }

  return previousData
}

export const combineQueriesWithPending = <T>(results: UseQueryResult<T>[]) => {
  const data = results.map((result) => result.data)

  const isLoading = results.map((result) => result.isLoading)
  const anyLoading = isLoading.some((loading) => loading)

  const isPending = results.map((result) => result.isPending)
  const anyPending = isPending.some((pending) => pending)

  const isFetching = results.map((result) => result.isFetching)
  const anyFetching = isFetching.some((fetching) => fetching)

  const isFetched = results.map((result) => result.isFetched)
  const allFetched = isFetched.every((fetched) => fetched)

  return {
    data,
    isLoading,
    anyLoading,
    isPending,
    anyPending,
    isFetching,
    anyFetching,
    isFetched,
    allFetched,
  }
}

export const combineArrayQueriesWithPending = <T>(
  results: UseQueryResult<T[]>[],
) => {
  const data = results.reduce((acc: Array<T>, result) => {
    return [...acc, ...(result.data ?? [])]
  }, [])

  const isLoading = results.map((result) => result.isLoading)
  const anyLoading = isLoading.some((loading) => loading)

  const isPending = results.map((result) => result.isPending)
  const anyPending = isPending.some((pending) => pending)

  const isFetching = results.map((result) => result.isFetching)
  const anyFetching = isFetching.some((fetching) => fetching)

  const isFetched = results.map((result) => result.isFetched)
  const allFetched = isFetched.every((fetched) => fetched)

  return {
    data,
    isLoading,
    anyLoading,
    isPending,
    anyPending,
    isFetching,
    anyFetching,
    isFetched,
    allFetched,
  }
}

interface Count<T> {
  type: T
  count: number
}

export const combineCounts = <T extends string>(
  result: UseQueryResult<Count<T>, Error>[],
) => {
  return {
    data: result.reduce<Record<T, number>>(
      (acc, curr) => {
        if (curr.data) {
          acc[curr.data.type] = curr.data.count
        }
        return acc
      },
      {} as Record<T, number>,
    ),
    totalCount: result.reduce((acc, curr) => acc + (curr.data?.count ?? 0), 0),
    isLoading: result.some((r) => r.isLoading),
    isFetching: result.some((r) => r.isFetching),
    isFetched: result.every((r) => r.isFetched),
  }
}
